import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  alpha,
  DialogContent,
  DialogActions,
  Drawer,
  Alert,
  useTheme,
  Chip,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet,
  AccountBalance,
  AttachMoney,
  AccountCircle,
  Event,
  // Business,
  // LocalShipping as CustomerIcon,
  // Receipt as PanIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { AgGridReact } from "ag-grid-react";
import { DataContext } from "../../context/DataContext";
import CustomerActionModal from "./CustomerActionModal";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

ModuleRegistry.registerModules([AllCommunityModule]);

// Optimized Debounce Hook
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Helper function to safely get string value
const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return String(value);
};

// Helper function to clean date string (remove single quote)
// const cleanDateString = (value) => {
//   const str = safeString(value);
//   if (!str) return '';
//   // Remove leading single quote if present
//   if (str.startsWith("'")) {
//     return str.substring(1);
//   }
//   return str;
// };

// Helper function to safely trim a string
// const safeTrim = (value) => {
//   const str = safeString(value);
//   return str.trim();
// };

// ActionsRenderer
const ActionsRenderer = (props) => {
  const handleEdit = () => {
    props.context.handleEditCustomer(props.data);
  };

  const handleDelete = () => {
    props.context.setDeleteCustomerId(props.data.id);
    props.context.setDeleteConfirmOpen(true);
  };

  const handleView = () => {
    props.context.setSelectedCustomer(props.data);
    props.context.setModalOpen(true);
  };

  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
      <Tooltip title="View Details">
        <IconButton size="small" onClick={handleView}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{ color: "#EF4444" }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const NameRenderer = (props) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
        {props.value}
      </Typography>
      {props.data.emailId && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          {props.data.emailId}
        </Typography>
      )}
    </Box>
  );
};

// Validation Schema - Updated with PanNo field
const CustomerSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name is too long")
    .required("Name is required"),
  customerShortcut: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  emailId: Yup.string()
    .email("Invalid email address")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  contact: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9+\-\s()]+$/, "Invalid contact number"),
  address: Yup.string()
    .max(500, "Address is too long")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  panNo: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  gstNo: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  dob: Yup.mixed()
    .nullable()
    .transform((value) => {
      if (value instanceof Date && isNaN(value.getTime())) return null;
      if (value === "" || value === undefined) return null;
      return value;
    })
    .test("is-valid-date", "Invalid date", (value) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    }),
  anniversary: Yup.mixed()
    .nullable()
    .transform((value) => {
      if (value instanceof Date && isNaN(value.getTime())) return null;
      if (value === "" || value === undefined) return null;
      return value;
    })
    .test("is-valid-date", "Invalid date", (value) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    }),
  openingBalanceCash: Yup.number()
    .typeError('Opening balance must be a number')
    .min(0, 'Balance cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  openingBalanceBank: Yup.number()
    .typeError('Opening balance must be a number')
    .min(0, 'Balance cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  openingBalanceFine: Yup.number()
    .typeError('Fine balance must be a number')
    .min(0, 'Fine cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  bankName: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  accountHolder: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  accountNumber: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  ifscCode: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  branch: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
});

const initialValues = {
  name: "",
  customerShortcut: "",
  emailId: "",
  contact: "",
  address: "",
  panNo: "",
  gstNo: "",
  dob: null,
  anniversary: null,
  openingBalanceCash: "",
  openingBalanceBank: "",
  openingBalanceFine: "",
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  ifscCode: "",
  branch: "",
};

// Customer Form Component
const CustomerForm = ({
  formik,
  isDark,
  isSaving,
  editingCustomer,
  handleCloseDrawer,
  handleInput,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Form>
      <Box sx={{ p: { xs: 2, sm: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                background: isDark
                  ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                  : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {editingCustomer ? "✏️ Edit Customer" : "👤 Add New Customer"}
            </Typography>
            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? '#1e293b' : '#ffffff',
                  isDark ? 0.4 : 0.7
                ),
                border: `1px solid ${alpha(
                  isDark ? '#475569' : '#e2e8f0',
                  isDark ? 0.2 : 0.4
                )}`,
                color: isDark ? '#e2e8f0' : '#475569',
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? '#1e293b' : '#ffffff',
                    isDark ? 0.6 : 0.9
                  ),
                  transform: 'scale(1.05)',
                },
              }}
              disabled={isSaving}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? "#94a3b8" : "#64748b",
              fontWeight: 400,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            {editingCustomer
              ? "Update customer information"
              : "Enter customer details"}
          </Typography>
        </Box>

        {/* Form Content - Scrollable Area */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, mb: 3, pt: 2 }}>
          {/* Personal Details Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#3b82f6" : "#10b981",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <AccountCircle
                    sx={{
                      color: isDark ? "#60a5fa" : "#10b981",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  }}
                >
                  Personal Details
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Name <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <Field name="name">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter full name"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.7 : 0.9
                              ),
                            },
                            "&.Mui-focused": {
                              borderColor: isDark ? "#60a5fa" : "#3b82f6",
                              borderWidth: "1.5px",
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.7 : 0.9
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Customer Shortcut
                  </Typography>
                  <Field name="customerShortcut">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter shortcut (e.g., REL, TIT, JOH)"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Email Address
                  </Typography>
                  <Field name="emailId">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="customer@example.com"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Contact Number <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <Field name="contact">
                    {({ field, form, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="text"
                        placeholder="+91 98765 43210"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        onChange={(e) => handleInput(e, "contact", form)}
                        onKeyDown={(e) => {
                          if (/[a-zA-Z]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        inputMode="tel"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    PAN Number
                  </Typography>
                  <Field name="panNo">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter PAN number (e.g., ABCDE1234F)"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    GST Number
                  </Typography>
                  <Field name="gstNo">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter GST number"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    Address
                  </Typography>
                  <Field name="address">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Street, City, State, Pincode"
                        multiline
                        rows={isMobile ? 2 : 3}
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            padding: { xs: "8px 10px", sm: "10px 12px" },
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Opening Balances Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#f59e0b" : "#f59e0b",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <AccountBalanceWallet
                    sx={{
                      color: isDark ? "#fbbf24" : "#f59e0b",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  }}
                >
                  Opening Balances
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Opening Balance (Cash)
                    </Typography>
                    <Field name="openingBalanceCash">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="0.00"
                          disabled={isSaving || !!editingCustomer}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          onChange={(e) => handleInput(e, "openingBalanceCash", form)}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                              '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                              'ArrowUp', 'ArrowDown', 'Tab'
                            ];
                            if (!allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                            if (e.key === '.' && field.value.includes('.')) {
                              e.preventDefault();
                            }
                          }}
                          inputMode="decimal"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoney
                                  sx={{
                                    fontSize: 16,
                                    color: isDark ? "#fbbf24" : "#f59e0b",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#fbbf24" : "#f59e0b",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Opening Balance (Bank)
                    </Typography>
                    <Field name="openingBalanceBank">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="0.00"
                          disabled={isSaving || !!editingCustomer}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          onChange={(e) => handleInput(e, "openingBalanceBank", form)}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                              '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                              'ArrowUp', 'ArrowDown', 'Tab'
                            ];
                            if (!allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                            if (e.key === '.' && field.value.includes('.')) {
                              e.preventDefault();
                            }
                          }}
                          inputMode="decimal"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountBalance
                                  sx={{
                                    fontSize: 16,
                                    color: isDark ? "#fbbf24" : "#f59e0b",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#fbbf24" : "#f59e0b",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Opening Balance (Fine in gms)
                    </Typography>
                    <Field name="openingBalanceFine">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="0.00"
                          disabled={isSaving || !!editingCustomer}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          onChange={(e) => handleInput(e, "openingBalanceFine", form)}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                              '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                              'ArrowUp', 'ArrowDown', 'Tab'
                            ];
                            if (!allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                            if (e.key === '.' && field.value.includes('.')) {
                              e.preventDefault();
                            }
                          }}
                          inputMode="decimal"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountBalance
                                  sx={{
                                    fontSize: 16,
                                    color: isDark ? "#fbbf24" : "#f59e0b",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#fbbf24" : "#f59e0b",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                sx={{
                  mt: 1.5,
                  display: "block",
                  color: isDark ? "#94A3B8" : "#64748B",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  fontStyle: "italic",
                }}
              >
                {editingCustomer ? "Opening Balance can only be set while creating a new customer, you can't change it." : "Optional: Initial balances for customer account. Leave as 0 if none, CANNOT be updated later."}
              </Typography>
            </CardContent>
          </Card>

          {/* Bank Details Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#8b5cf6" : "#8b5cf6",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <AccountBalanceWallet
                    sx={{
                      color: isDark ? "#a78bfa" : "#8b5cf6",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  }}
                >
                  Bank Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Bank Name
                    </Typography>
                    <Field name="bankName">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter bank name"
                          disabled={isSaving}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Account Holder Name
                    </Typography>
                    <Field name="accountHolder">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter account holder name"
                          disabled={isSaving}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Account Number
                    </Typography>
                    <Field name="accountNumber">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter account number"
                          disabled={isSaving}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      IFSC Code
                    </Typography>
                    <Field name="ifscCode">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter IFSC code"
                          disabled={isSaving}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Branch Name
                    </Typography>
                    <Field name="branch">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter branch name"
                          disabled={isSaving}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: { xs: "0.813rem", sm: "0.875rem" },
                              padding: { xs: "8px 10px", sm: "10px 12px" },
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional Details Card */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#8b5cf6" : "#8b5cf6",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <Event
                    sx={{
                      color: isDark ? "#a78bfa" : "#8b5cf6",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  }}
                >
                  Additional Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Date of Birth
                    </Typography>
                    <Field name="dob">
                      {({ field, form, meta }) => (
                        <DatePicker
                          value={field.value}
                          onChange={(date) =>
                            form.setFieldValue(field.name, date)
                          }
                          onBlur={field.onBlur}
                          disabled={isSaving}
                          enableAccessibleFieldDOMStructure={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: meta.touched && !!meta.error,
                              helperText: meta.touched && meta.error,
                              placeholder: "DD/MM/YYYY",
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#ffffff",
                                    isDark ? 0.6 : 0.8
                                  ),
                                  border: `1px solid ${alpha(
                                    isDark ? "#475569" : "#e2e8f0",
                                    isDark ? 0.3 : 0.4
                                  )}`,
                                  "&:hover": {
                                    borderColor: alpha(
                                      isDark ? "#60a5fa" : "#3b82f6",
                                      isDark ? 0.5 : 0.6
                                    ),
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                  "&.Mui-focused": {
                                    borderColor: isDark ? "#60a5fa" : "#3b82f6",
                                    borderWidth: "1.5px",
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                                  padding: { xs: "8px 10px", sm: "10px 12px" },
                                },
                                "& .MuiFormHelperText-root": {
                                  marginLeft: 0,
                                  fontSize: "0.75rem",
                                },
                              },
                            },
                            field: {
                              clearable: true,
                              format: "dd/MM/yyyy",
                            },
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiIconButton-root": {
                              color: isDark ? "#60a5fa" : "#3b82f6",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      }}
                    >
                      Anniversary Date
                    </Typography>
                    <Field name="anniversary">
                      {({ field, form, meta }) => (
                        <DatePicker
                          value={field.value}
                          onChange={(date) =>
                            form.setFieldValue(field.name, date)
                          }
                          onBlur={field.onBlur}
                          disabled={isSaving}
                          enableAccessibleFieldDOMStructure={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: meta.touched && !!meta.error,
                              helperText: meta.touched && meta.error,
                              placeholder: "DD/MM/YYYY",
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#ffffff",
                                    isDark ? 0.6 : 0.8
                                  ),
                                  border: `1px solid ${alpha(
                                    isDark ? "#475569" : "#e2e8f0",
                                    isDark ? 0.3 : 0.4
                                  )}`,
                                  "&:hover": {
                                    borderColor: alpha(
                                      isDark ? "#60a5fa" : "#3b82f6",
                                      isDark ? 0.5 : 0.6
                                    ),
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                  "&.Mui-focused": {
                                    borderColor: isDark ? "#60a5fa" : "#3b82f6",
                                    borderWidth: "1.5px",
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                                  padding: { xs: "8px 10px", sm: "10px 12px" },
                                },
                                "& .MuiFormHelperText-root": {
                                  marginLeft: 0,
                                  fontSize: "0.75rem",
                                },
                              },
                            },
                            field: {
                              clearable: true,
                              format: "dd/MM/yyyy",
                            },
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiIconButton-root": {
                              color: isDark ? "#60a5fa" : "#3b82f6",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                sx={{
                  mt: 1.5,
                  display: "block",
                  color: isDark ? "#94A3B8" : "#64748B",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  fontStyle: "italic",
                }}
              >
                Optional: Used for sending birthday/anniversary greetings
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            pt: 2,
            borderTop: `1px solid ${alpha(isDark ? "#334155" : "#e2e8f0", 0.2)}`,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseDrawer}
              disabled={isSaving}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: alpha(
                  isDark ? "#1e293b" : "#ffffff",
                  isDark ? 0.4 : 0.8
                ),
                border: `1.5px solid ${alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                )}`,
                color: isDark ? "#e2e8f0" : "#475569",
                py: 1.25,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.6 : 0.95
                  ),
                  borderColor: alpha(
                    isDark ? "#60a5fa" : "#3b82f6",
                    isDark ? 0.5 : 0.6
                  ),
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  borderColor: alpha(isDark ? "#475569" : "#cbd5e1", 0.2),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSaving || !formik.isValid}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                background: isDark
                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                  : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: isDark
                  ? "0 4px 12px rgba(59, 130, 246, 0.25)"
                  : "0 4px 12px rgba(37, 99, 235, 0.25)",
                color: "#fff",
                py: 1.25,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                transition: "all 0.2s ease",
                "&:hover": {
                  background: isDark
                    ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                    : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                  boxShadow: isDark
                    ? "0 6px 16px rgba(59, 130, 246, 0.35)"
                    : "0 6px 16px rgba(37, 99, 235, 0.35)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  background: alpha(isDark ? "#3b82f6" : "#2563eb", 0.3),
                  boxShadow: "none",
                  color: alpha("#fff", 0.5),
                },
              }}
            >
              {isSaving ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  <span>Saving...</span>
                </Box>
              ) : editingCustomer ? (
                "Update Customer"
              ) : (
                "Save Customer"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Form>
  );
};

const CustomersPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    customers,
    loading: contextLoading,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    refresh,
  } = useContext(DataContext);

  // AG Grid ref
  const gridRef = useRef();

  // State for local loading states
  const [isPageLoading, setIsPageLoading] = useState(false);
  const isSearching = false;
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isGridLoading = false;

  const [searchInput, setSearchInput] = useState("");
  const searchTerm = useDebounce(searchInput, 300);
  const [selectedLetter, setSelectedLetter] = useState("ALL");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const [modalOpen, setModalOpen] = useState(false);
  const [isGridReady, setIsGridReady] = useState(false);

  const onGridReady = (params) => {
    gridRef.current = params;
    setIsGridReady(true);
  };

  // Row selection state
  const [selectedRows, setSelectedRows] = useState([]);

  // Handle row selection
  const handleRowSelect = useCallback((customerId, isSelected) => {
    setSelectedRows((prev) => {
      if (isSelected) {
        return [...prev, customerId];
      } else {
        return prev.filter((id) => id !== customerId);
      }
    });
  }, []);

  const handleEditCustomer = useCallback((customer) => {
    setEditingCustomer(customer);
    setIsDrawerLoading(true);

    setTimeout(() => {
      setDrawerOpen(true);
      setIsDrawerLoading(false);
    }, 100);
  }, []);

  // HANDLE INPUT FUNCTION
  const handleInput = useCallback((e, fieldName, formik) => {
    const value = e.target.value;

    // Handle contact fields
    if (fieldName === 'contact') {
      const filteredValue = value.replace(/[^0-9+\-\s()]/g, '');
      formik.setFieldValue(fieldName, filteredValue);
    }
    // Handle opening balance fields (cash, bank, fine)
    else if (fieldName === 'openingBalanceCash' ||
      fieldName === 'openingBalanceBank' ||
      fieldName === 'openingBalanceFine') {
      let filteredValue = value.replace(/[^0-9.]/g, '');
      const parts = filteredValue.split('.');
      if (parts.length > 2) {
        filteredValue = parts[0] + '.' + parts.slice(1).join('');
      }
      formik.setFieldValue(fieldName, filteredValue);
    }
    // For all other fields, allow any input
    else {
      formik.setFieldValue(fieldName, value);
    }
  }, []);

  const handleCloseDrawer = useCallback(() => {
    if (!isSaving) {
      setDrawerOpen(false);
    }
  }, [isSaving]);

  // Column Definitions for AG Grid
  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        headerName: "Sr. No.",
        valueGetter: "node.rowIndex + 1",
        width: isMobile ? 40 : 50,
        sortable: false,
        filter: false,
        pinned: isMobile ? false : "left",
        cellStyle: { textAlign: "center", fontWeight: 600 },
      },
      {
        field: "name",
        headerName: "Name",
        width: isMobile ? 180 : 250,
        filter: "agTextColumnFilter",
        sortable: true,
        sort: "asc",
        cellRenderer: NameRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
        flex: 0.6,
        minWidth: isMobile ? 70 : 180,
      },
      {
        field: "customerShortcut",
        headerName: "Shortcut",
        width: isMobile ? 70 : 100,
        filter: "agTextColumnFilter",
        sortable: true,
        cellRenderer: (params) => (
          <Chip
            label={params.value || '—'}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha('#8b5cf6', 0.1)
                : alpha('#8b5cf6', 0.1),
              border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
              color: '#8b5cf6',
              fontWeight: 600,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              letterSpacing: '0.5px',
            }}
          />
        ),
      },
      {
        field: "contact",
        headerName: "Contact",
        width: isMobile ? 120 : 150,
        filter: "agTextColumnFilter",
        sortable: true,
        flex: 0.6,
        minWidth: isMobile ? 70 : 120,
      },
    ];

    // Only show address on desktop/tablet
    if (!isMobile) {
      baseColumns.push({
        field: "address",
        headerName: "Address",
        width: isTablet ? 150 : 250,
        filter: "agTextColumnFilter",
        sortable: true,
        cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
        autoHeight: true,
        flex: 0.6,
        minWidth: isMobile ? 70 : 160,
      });
    }

    baseColumns.push(
      {
        field: "panNo",
        headerName: "PAN",
        width: isMobile ? 80 : 120,
        filter: "agTextColumnFilter",
        sortable: true,
        flex: 0.6,
        minWidth: isMobile ? 70 : 120,
        cellRenderer: (params) => (
          <Chip
            label={params.value || 'No PAN'}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha('#f59e0b', 0.1)
                : alpha('#f59e0b', 0.1),
              border: `1px solid ${alpha('#f59e0b', 0.2)}`,
              color: '#d97706',
              fontWeight: 500,
              fontSize: '0.75rem',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        ),
      },
      {
        field: "gstNo",
        headerName: "GST",
        width: isMobile ? 80 : 120,
        filter: "agTextColumnFilter",
        sortable: true,
        cellRenderer: (params) => (
          <Chip
            label={params.value || 'No GST'}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha('#8b5cf6', 0.1)
                : alpha('#8b5cf6', 0.1),
              border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
              color: '#8b5cf6',
              fontWeight: 500,
              fontSize: '0.75rem',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        ),
        flex: 0.6,
        minWidth: isMobile ? 90 : 140,
      },
      {
        field: "dob",
        headerName: "DOB",
        width: isMobile ? 70 : 120,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => {
          try {
            let dateStr = safeString(params.value);
            if (!dateStr || dateStr === 'null' || dateStr === 'undefined' || dateStr === '') return '';
            // Clean the string (remove single quote)
            if (dateStr.startsWith("'")) {
              dateStr = dateStr.substring(1);
            }
            // Check if it's a valid date format
            if (dateStr.includes('/')) {
              const [day, month, year] = dateStr.split("/");
              const date = new Date(year, month - 1, day);
              if (!isNaN(date.getTime())) {
                return format(date, isMobile ? "dd/MM/yyyy" : "dd MMM yyyy");
              }
            }
            return '';
          } catch {
            return '';
          }
        },
        sortable: true,
        flex: 0.5,
        minWidth: isMobile ? 70 : 120,
      },
      {
        field: "anniversary",
        headerName: "Anniversary",
        width: isMobile ? 70 : 120,
        filter: "agDateColumnFilter",
        sortable: true,
        valueFormatter: (params) => {
          try {
            let dateStr = safeString(params.value);
            if (!dateStr || dateStr === 'null' || dateStr === 'undefined' || dateStr === '') return '';
            // Clean the string (remove single quote)
            if (dateStr.startsWith("'")) {
              dateStr = dateStr.substring(1);
            }
            // Check if it's a valid date format
            if (dateStr.includes('/')) {
              const [day, month, year] = dateStr.split("/");
              const date = new Date(year, month - 1, day);
              if (!isNaN(date.getTime())) {
                return format(date, isMobile ? "dd/MM/yyyy" : "dd MMM yyyy");
              }
            }
            return '';
          } catch {
            return '';
          }
        },
        flex: 0.5,
        minWidth: isMobile ? 70 : 120,
      }
    );

    // Only show balance columns on tablet and desktop
    if (!isMobile) {
      baseColumns.push(
        {
          field: "openingBalanceCash",
          headerName: "Opening Cash",
          width: isTablet ? 80 : 100,
          filter: "agNumberColumnFilter",
          sortable: true,
          valueFormatter: (params) => {
            return params.value ? `₹${params.value}` : "₹0";
          },
        },
        {
          field: "openingBalanceBank",
          headerName: "Opening Bank",
          width: isTablet ? 80 : 100,
          filter: "agNumberColumnFilter",
          sortable: true,
          valueFormatter: (params) => {
            return params.value ? `₹${params.value}` : "₹0";
          },
        }
      );
    }

    baseColumns.push({
      headerName: "Actions",
      width: isMobile ? 100 : 150,
      filter: false,
      sortable: false,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        context: {
          handleEditCustomer,
          setDeleteCustomerId,
          setDeleteConfirmOpen,
          setSelectedCustomer,
          setModalOpen,
        },
      },
      pinned: isMobile ? false : "right",
    });

    return baseColumns;
  }, [handleEditCustomer, isDark, isMobile, isTablet]);

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: isMobile ? 40 : 100,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        borderRight: isDark
          ? "1px solid rgba(255, 255, 255, 0.05)"
          : "1px solid rgba(224, 224, 224, 0.5)",
        display: "flex",
        alignItems: "center",
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      },
    }),
    [isDark, isMobile]
  );

  // Grid options
  const gridOptions = useMemo(
    () => ({
      rowBuffer: 20,
      cacheBlockSize: 100,
      maxBlocksInCache: 10,
      animateRows: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      suppressColumnVirtualisation: false,
      suppressRowVirtualisation: false,
    }),
    []
  );

  // Handle search filtering
  useEffect(() => {
    if (!isGridReady || !gridRef.current?.api) return;

    const timer = setTimeout(() => {
      try {
        if (searchTerm.trim()) {
          const filterModel = {};
          setSelectedLetter("ALL");
          const searchableColumns = ["name"];

          searchableColumns.forEach((column) => {
            filterModel[column] = {
              type: "contains",
              filter: searchTerm,
              filterParams: {
                caseSensitive: false,
              },
            };
          });

          gridRef.current.api.setFilterModel(filterModel);
        } else {
          gridRef.current.api.setFilterModel(null);
          if (selectedLetter !== "ALL") {
            gridRef.current.api.setFilterModel({
              name: {
                type: "startsWith",
                filter: selectedLetter,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error applying search filter:", error);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isGridReady, selectedLetter]);

  // Memoize available letters
  const availableLetters = useMemo(() => {
    if (!customers || customers.length === 0) return [];
    const letters = new Set();
    customers.forEach((customer) => {
      if (customer.name && customer.name.length > 0) {
        letters.add(customer.name.charAt(0).toUpperCase());
      }
    });
    return Array.from(letters).sort();
  }, [customers]);

  const handleAddCustomer = useCallback(() => {
    setEditingCustomer(null);
    setDrawerOpen(true);
  }, []);

  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  const handleSaveCustomer = useCallback(
    async (values) => {
      setIsSaving(true);
      try {
        // Format dates to DD/MM/YYYY string format before saving
        const formatDate = (date) => {
          if (!date) return '';

          try {
            if (typeof date === 'string') {
              let cleanStr = date;
              if (cleanStr.startsWith("'")) {
                cleanStr = cleanStr.substring(1);
              }
              if (cleanStr.includes('/')) {
                const parts = cleanStr.split('/');
                if (parts.length === 3 && parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
                  return `'${cleanStr}`;
                }
              }
              const parsedDate = new Date(cleanStr);
              if (!isNaN(parsedDate.getTime())) {
                const formatted = format(parsedDate, "dd/MM/yyyy");
                return `'${formatted}`;
              }
              return '';
            }
            if (date instanceof Date && !isNaN(date.getTime())) {
              const formatted = format(date, "dd/MM/yyyy");
              return `'${formatted}`;
            }
            return '';
          } catch (error) {
            console.error('Error formatting date:', error);
            return '';
          }
        };

        // Build formatted values - keep empty strings as empty strings, not null
        const formattedValues = {
          name: values.name || '',
          customerShortcut: values.customerShortcut || '',
          emailId: values.emailId || '',
          contact: values.contact || '',
          address: values.address || '',
          panNo: values.panNo || '',
          gstNo: values.gstNo || '',
          openingBalanceCash: values.openingBalanceCash || 0,
          openingBalanceBank: values.openingBalanceBank || 0,
          openingBalanceFine: values.openingBalanceFine || 0,
          bankName: values.bankName || '',
          accountHolder: values.accountHolder || '',
          accountNumber: values.accountNumber || '',
          ifscCode: values.ifscCode || '',
          branch: values.branch || '',
          dob: formatDate(values.dob),
          anniversary: formatDate(values.anniversary),
        };

        let result;

        if (editingCustomer) {
          formattedValues.currentBalanceCash = editingCustomer.currentBalanceCash || formattedValues.openingBalanceCash || 0;
          formattedValues.currentBalanceBank = editingCustomer.currentBalanceBank || formattedValues.openingBalanceBank || 0;
          formattedValues.currentBalanceFine = editingCustomer.currentBalanceFine || formattedValues.openingBalanceFine || 0;

          result = await updateCustomer(editingCustomer.id, formattedValues);

          if (result?.success) {
            showAlert(
              result.message || "Customer updated successfully",
              "success"
            );
            setDrawerOpen(false);
          } else {
            showAlert(
              result?.message || result?.error || "Failed to update customer",
              "error"
            );
          }
        } else {
          formattedValues.currentBalanceCash = formattedValues.openingBalanceCash || 0;
          formattedValues.currentBalanceBank = formattedValues.openingBalanceBank || 0;
          formattedValues.currentBalanceFine = formattedValues.openingBalanceFine || 0;

          result = await addCustomer(formattedValues);

          if (result?.success) {
            showAlert(
              result.message || "Customer added successfully",
              "success"
            );
            setDrawerOpen(false);
          } else {
            showAlert(
              result?.message || result?.error || "Failed to add customer",
              "error"
            );
          }
        }
      } catch (error) {
        console.error("Error saving customer:", error);
        showAlert("An unexpected error occurred", "error");
      } finally {
        setIsSaving(false);
      }
    },
    [editingCustomer, showAlert, addCustomer, updateCustomer]
  );

  const handleDeleteCustomer = useCallback(
    async (id) => {
      try {
        const result = await deleteCustomer(id);

        if (result?.success) {
          showAlert(
            result.message || "Customer deleted successfully",
            "success"
          );
        } else {
          showAlert(
            result?.message || result?.error || "Failed to delete customer",
            "error"
          );
        }
      } catch (error) {
        console.error("Error deleting customer:", error);
        showAlert("An unexpected error occurred", "error");
      }
      setDeleteConfirmOpen(false);
      setDeleteCustomerId(null);
    },
    [deleteCustomer, showAlert]
  );

  const handleRefresh = useCallback(async () => {
    setIsPageLoading(true);
    try {
      await refresh();
      showAlert("Data refreshed successfully", "success");
    } catch (error) {
      showAlert("Failed to refresh data", "error");
    } finally {
      setTimeout(() => setIsPageLoading(false), 500);
    }
  }, [refresh, showAlert]);

  const getInitialValues = useCallback(() => {
    if (editingCustomer) {
      const parseDate = (dateString) => {
        if (!dateString) return null;
        try {
          const str = safeString(dateString);
          if (!str || str === 'null' || str === 'undefined') return null;

          let cleanStr = str;
          if (cleanStr.startsWith("'")) {
            cleanStr = cleanStr.substring(1);
          }
          cleanStr = cleanStr.replace(/^['"]|['"]$/g, '');

          if (!cleanStr || cleanStr === 'null' || cleanStr === 'undefined') return null;

          let day, month, year;

          if (cleanStr.includes('/')) {
            const parts = cleanStr.split('/');
            if (parts.length === 3) {
              day = parseInt(parts[0], 10);
              month = parseInt(parts[1], 10) - 1;
              year = parseInt(parts[2], 10);
            }
          } else if (cleanStr.includes('-')) {
            const parts = cleanStr.split('-');
            if (parts.length === 3) {
              year = parseInt(parts[0], 10);
              month = parseInt(parts[1], 10) - 1;
              day = parseInt(parts[2], 10);
            }
          } else {
            const timestamp = Date.parse(cleanStr);
            if (!isNaN(timestamp)) {
              const date = new Date(timestamp);
              if (!isNaN(date.getTime())) {
                return date;
              }
            }
            return null;
          }

          if (day && month !== undefined && year) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year) {
              return date;
            }
          }

          return null;
        } catch (error) {
          console.error('Error parsing date:', error);
          return null;
        }
      };

      return {
        name: editingCustomer?.name || '',
        customerShortcut: editingCustomer?.customerShortcut || '',
        emailId: editingCustomer?.emailId || '',
        contact: editingCustomer?.contact || '',
        address: editingCustomer?.address || '',
        panNo: editingCustomer?.panNo || '',
        gstNo: editingCustomer?.gstNo || '',
        dob: parseDate(editingCustomer?.dob),
        anniversary: parseDate(editingCustomer?.anniversary),
        openingBalanceCash: editingCustomer?.openingBalanceCash || '',
        openingBalanceBank: editingCustomer?.openingBalanceBank || '',
        openingBalanceFine: editingCustomer?.openingBalanceFine || '',
        bankName: editingCustomer?.bankName || '',
        accountHolder: editingCustomer?.accountHolder || '',
        accountNumber: editingCustomer?.accountNumber || '',
        ifscCode: editingCustomer?.ifscCode || '',
        branch: editingCustomer?.branch || '',
      };
    }
    return initialValues;
  }, [editingCustomer]);

  // Custom loading overlay
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#2563EB" }} />
        </Box>
      );
    };
  }, []);

  // Custom no rows overlay
  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: isDark ? "#a0a0a0" : "#666" }}>
            {searchTerm
              ? "No customers found matching your search"
              : "No customers found"}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchTerm]);

  const isLoading = contextLoading || isPageLoading;
  const isFilteringOrSearching = isSearching || isGridLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={isMobile ? 40 : 60}
            thickness={4}
            sx={{ color: "#2563EB", mb: 2 }}
          />
          <Typography
            sx={{
              fontSize: { xs: "0.9rem", sm: "1.1rem" },
              fontWeight: 600,
              color: isDark ? "#e0e0e0" : "#1a1a1a",
            }}
          >
            Loading customers...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          py: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 0 },
          color: isDark ? "#e0e0e0" : "#1a1a1a",
        }}
      >
        <Backdrop
          open={isDrawerLoading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="primary" />
        </Backdrop>

        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Alert */}
          {alertMessage && (
            <Alert
              severity={alertType}
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setAlertMessage("")}
            >
              {alertMessage}
            </Alert>
          )}

          {/* Header Section */}
          <Card
            sx={{
              mb: 2,
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(255, 255, 255, 0.2)",
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 2 }}
                sx={{ alignItems: { xs: "stretch", sm: "center" } }}
              >
                <Box sx={{ mb: { xs: 0, sm: 3 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                      mb: 0.5,
                      color: isDark ? "#e0e0e0" : "#1a1a1a",
                    }}
                  >
                    Customers
                  </Typography>
                  <Typography
                    sx={{
                      color: isDark ? "#a0a0a0" : "#666",
                      fontSize: { xs: "0.8rem", sm: "1rem" },
                    }}
                  >
                    Total Customers:{" "}
                    <Chip
                      label={customers.length}
                      color="primary"
                      size="small"
                    />
                  </Typography>
                </Box>

                <TextField
                  placeholder="Search customers..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            color: isDark ? "#a0a0a0" : "#666",
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    flex: 1,
                    minWidth: isMobile ? "auto" : 250,
                    "& .MuiOutlinedInput-root": {
                      color: isDark ? "#e0e0e0" : "#1a1a1a",
                      "& fieldset": {
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                />

                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  variant="outlined"
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    borderColor: "#2563EB",
                    color: "#121826",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(37, 99, 235, 0.15)"
                        : "rgba(37, 99, 235, 0.08)",
                    },
                  }}
                >
                  Refresh
                </Button>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddCustomer}
                  variant="contained"
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Customer
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Alphabet Filter */}
          <Card
            sx={{
              mb: 2,
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(255, 255, 255, 0.2)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  alignItems: "center",
                  overflowX: "auto",
                  pb: { xs: 0.5, sm: 0 },
                  "&::-webkit-scrollbar": {
                    height: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: isDark ? "#1e293b" : "#e2e8f0",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: isDark ? "#475569" : "#94a3b8",
                    borderRadius: "10px",
                  },
                }}
              >
                <Button
                  onClick={() => setSelectedLetter("ALL")}
                  size="small"
                  variant={selectedLetter === "ALL" ? "contained" : "outlined"}
                  sx={{
                    background:
                      selectedLetter === "ALL"
                        ? "linear-gradient(135deg, #2563EB, #1D4ED8)"
                        : "transparent",
                    color: selectedLetter === "ALL" ? "#fff" : "#000",
                    borderColor: "#2563EB",
                    minWidth: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    fontWeight: 600,
                    padding: { xs: "4px 6px", sm: "4px 8px" },
                    flexShrink: 0,
                    border:
                      selectedLetter === "ALL"
                        ? "none"
                        : "1px solid rgb(26, 29, 35)",
                  }}
                >
                  ALL
                </Button>
                {availableLetters.map((letter) => (
                  <Button
                    key={letter}
                    onClick={() => setSelectedLetter(letter)}
                    size="small"
                    variant={
                      selectedLetter === letter ? "contained" : "outlined"
                    }
                    sx={{
                      background:
                        selectedLetter === letter
                          ? "linear-gradient(135deg, #2563EB, #1D4ED8)"
                          : "transparent",
                      color: selectedLetter === letter ? "#fff" : "#000",
                      borderColor: "#2563EB",
                      minWidth: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      fontWeight: 600,
                      padding: { xs: "4px 6px", sm: "4px 6px" },
                      flexShrink: 0,
                      border:
                        selectedLetter === letter
                          ? "none"
                          : "1px solid rgb(26, 29, 35)",
                      borderRadius: "6px",
                    }}
                  >
                    {letter}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* AG Grid Table */}
          <Card
            elevation={0}
            sx={{
              borderRadius: { xs: "12px", sm: "20px" },
              overflow: "hidden",
              position: "relative",
              height: isMobile ? "calc(100vh - 280px)" : "calc(100vh - 400px)",
              minHeight: { xs: "400px", sm: "500px" },
              background: isDark
                ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
                : "linear-gradient(180deg, #ffffff 0%,rgba(248, 250, 252, 0.26) 100%)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(0, 0, 0, 0.04)",
              boxShadow: isDark
                ? "0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
                : "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8) inset",
            }}
          >
            {isFilteringOrSearching && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  zIndex: 10,
                  background:
                    "linear-gradient(90deg, transparent, #2563eb, #8b5cf6, #2563eb, transparent)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite linear",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "100% 0" },
                    "100%": { backgroundPosition: "-100% 0" },
                  },
                }}
              />
            )}

            <CardContent sx={{ p: 0, height: "100%" }}>
              <Box
                className={isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
                sx={{
                  width: "100%",
                  height: "100%",
                  "& .ag-root-wrapper": {
                    border: "none !important",
                    borderRadius: { xs: "12px", sm: "20px" },
                  },
                  "& .ag-header": {
                    background: isDark
                      ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%) !important"
                      : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%) !important",
                    borderBottom: isDark
                      ? "1px solid rgba(255, 255, 255, 0.08) !important"
                      : "1px solid rgba(0, 0, 0, 0.06) !important",
                  },
                  "& .ag-header-cell": {
                    fontWeight: "700 !important",
                    fontSize: { xs: "10px !important", sm: "12px !important" },
                    textTransform: "uppercase !important",
                    letterSpacing: "0.8px !important",
                    color: isDark ? "#94a3b8 !important" : "#64748b !important",
                    borderRight: "none !important",
                    transition: "background 0.2s ease !important",
                    "&:hover": {
                      background: isDark
                        ? "rgba(37, 99, 235, 0.1) !important"
                        : "rgba(37, 99, 235, 0.05) !important",
                    },
                  },
                  "& .ag-body-viewport": {
                    background: isDark
                      ? "#16161a !important"
                      : "#ffffff !important",
                  },
                  "& .ag-row": {
                    borderBottom: isDark
                      ? "1px solid rgba(255, 255, 255, 0.04) !important"
                      : "1px solid rgba(0, 0, 0, 0.03) !important",
                    transition: "all 0.2s ease !important",
                    "&:hover": {
                      background: isDark
                        ? "linear-gradient(90deg, rgba(37, 99, 235, 0.08), rgba(139, 92, 246, 0.05)) !important"
                        : "linear-gradient(90deg, rgba(37, 99, 235, 0.04), rgba(139, 92, 246, 0.02)) !important",
                      transform: "scale(1.001)",
                    },
                  },
                  "& .ag-row-odd": {
                    background: isDark
                      ? "rgba(255, 255, 255, 0.01) !important"
                      : "rgba(248, 250, 252, 0.5) !important",
                  },
                  "& .ag-row-even": {
                    background: isDark
                      ? "transparent !important"
                      : "#ffffff !important",
                  },
                  "& .ag-cell": {
                    display: "flex !important",
                    alignItems: "center !important",
                    color: isDark ? "#e2e8f0 !important" : "#1e293b !important",
                    fontSize: { xs: "12px !important", sm: "14px !important" },
                    fontWeight: "500 !important",
                    borderRight: "none !important",
                    padding: { xs: "0 8px !important", sm: "0 16px !important" },
                  },
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={customers}
                  defaultColDef={defaultColDef}
                  gridOptions={gridOptions}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  rowHeight={isMobile ? 60 : 68}
                  headerHeight={isMobile ? 44 : 52}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  suppressContextMenu={true}
                  suppressMenuHide={false}
                  suppressMovableColumns={true}
                  context={{
                    selectedRows,
                    handleRowSelect,
                  }}
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit();
                    onGridReady(params);
                    window.addEventListener("resize", () => {
                      setTimeout(() => params.api.sizeColumnsToFit());
                    });
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Customer Action Modal */}
          <CustomerActionModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            customer={selectedCustomer}
            isDarkMode={isDark}
          />

          {/* Add/Edit Customer Drawer with Formik */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleCloseDrawer}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 2,
              "& .MuiDrawer-paper": {
                width: { xs: "100%", sm: 500, md: 550 },
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                background: isDark
                  ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
                borderLeft: `1px solid ${alpha(
                  isDark ? theme.palette.divider : "#3b82f6",
                  isDark ? 0.1 : 0.15
                )}`,
                boxShadow: isDark
                  ? "0 0 60px rgba(0, 0, 0, 0.4)"
                  : "0 0 60px rgba(59, 130, 246, 0.15)",
              },
            }}
          >
            <Formik
              initialValues={getInitialValues()}
              validationSchema={CustomerSchema}
              enableReinitialize
              onSubmit={(values) => handleSaveCustomer(values)}
            >
              {(formik) => (
                <CustomerForm
                  formik={formik}
                  isDark={isDark}
                  isSaving={isSaving}
                  editingCustomer={editingCustomer}
                  handleCloseDrawer={handleCloseDrawer}
                  handleInput={handleInput}
                />
              )}
            </Formik>
          </Drawer>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: { xs: "12px", sm: "16px" },
                margin: { xs: 1, sm: 2 },
                width: { xs: "calc(100% - 16px)", sm: "auto" },
              }
            }}
          >
            <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
              Delete Customer
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                Are you sure you want to delete this customer? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1 } }}>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
                variant="outlined"
                fullWidth={isMobile}
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteCustomer(deleteCustomerId)}
                variant="contained"
                color="error"
                fullWidth={isMobile}
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default React.memo(CustomersPage);